import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getMilestones, saveMilestone } from '../lib/api';
import type { MilestoneProgress } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { Chip } from '../components/Chips';
import { shortDate } from '../lib/format';

const STATUSES = ['planned', 'active', 'shipped', 'cancelled'];

function Bar({ m }: { m: MilestoneProgress }) {
  const total = Math.max(m.items, 1);
  const pct = (n: number) => `${Math.round((n / total) * 100)}%`;
  return (
    <div className="bar" title={`${m.completed} done, ${m.in_flight} in flight, ${m.cancelled} cancelled, ${m.remaining} to go`}>
      <i className="b-done" style={{ width: pct(m.completed) }} />
      <i className="b-flight" style={{ width: pct(m.in_flight) }} />
      <i className="b-cancel" style={{ width: pct(m.cancelled) }} />
    </div>
  );
}

export default function MilestonesPage() {
  const { role } = useAuth();
  const canEdit = role === 'developer';
  const { data, error, loading, reload } = useAsync(getMilestones, []);
  const [creating, setCreating] = useState(false);
  const [key, setKey] = useState('');
  const [title, setTitle] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setSaveError(null);
    try {
      await saveMilestone({ key: key.trim(), title: title.trim(), target_date: target || null, status: 'planned' });
      setKey('');
      setTitle('');
      setTarget('');
      setCreating(false);
      reload();
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setStatusOf(m: MilestoneProgress, status: string) {
    setSaveError(null);
    try {
      await saveMilestone({ key: m.key, status });
      reload();
    } catch (err) {
      setSaveError((err as Error).message);
    }
  }

  if (loading && !data) return <p className="loading">Loading milestones...</p>;
  if (error) return <div className="error">{error}</div>;

  const list = data?.milestones ?? [];

  return (
    <>
      <div className="detail-head">
        <h1>Milestones</h1>
        {canEdit ? (
          <button className="ghost" onClick={() => setCreating((c) => !c)}>
            {creating ? 'Cancel' : 'New milestone'}
          </button>
        ) : null}
      </div>
      <p className="sub">
        A release or a named piece of a project. No time boxes: a milestone ships when its work is done.
      </p>

      {saveError ? <div className="error">{saveError}</div> : null}

      {creating ? (
        <form className="panel" onSubmit={create} style={{ marginBottom: 18 }}>
          <div className="row">
            <span style={{ flex: 1 }}>
              <label htmlFor="ms-title">Title</label>
              <input
                id="ms-title"
                type="text"
                placeholder="Agent 1.2.200, Heuvel1 test week, Booking queue v2"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!key) return;
                }}
              />
            </span>
            <span>
              <label htmlFor="ms-key">Key</label>
              <input
                id="ms-key"
                type="text"
                placeholder="agent-1-2-200"
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
            </span>
            <span>
              <label htmlFor="ms-target">Target date (optional)</label>
              <input id="ms-target" type="date" value={target} onChange={(e) => setTarget(e.target.value)} />
            </span>
          </div>
          <button disabled={busy || !key.trim() || !title.trim()} style={{ marginTop: 14 }}>
            Create
          </button>
        </form>
      ) : null}

      {!list.length ? (
        <p className="empty">
          No milestones yet. {canEdit ? 'Create one, then drop work into it from an item page.' : ''}
        </p>
      ) : null}

      {list.map((m) => (
        <div className="ms-row" key={m.id}>
          <div>
            <div className="rtitle">
              <Link to={`/?milestone=${m.id}`}>{m.title}</Link> <Chip>{m.status}</Chip>
              {m.target_date ? <Chip>by {shortDate(m.target_date)}</Chip> : null}
            </div>
            <div className="rpath">
              {m.key}
              {m.components?.length ? `, ${m.components.join(', ')}` : ''}
            </div>
            <Bar m={m} />
            <div className="rdesc">
              {m.completed} of {m.items} done
              {m.in_flight ? `, ${m.in_flight} in flight` : ''}
              {m.remaining ? `, ${m.remaining} to go` : ''}
              {m.cancelled ? `, ${m.cancelled} cancelled` : ''}
            </div>
          </div>
          {canEdit ? (
            <select value={m.status} onChange={(e) => setStatusOf(m, e.target.value)} style={{ width: 'auto' }}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      ))}
    </>
  );
}
